<template>
  <div class="flex flex-col items-start">
    <ClientOnly>
      <VueSelect
        :id="id"
        ref="selectRef"
        v-model="selectedSchool"
        class="select w-full"
        :class="[
          props.type === 'large' ? 'select-large' : '',
          props.icon === 'search' ? 'select-search' : '',
        ]"
        :clear-search-on-blur="() => !hasTouch"
        :disabled="props.disabled"
        :filterable="false"
        label="name"
        :loading="props.loading"
        :options="props.schools"
        :placeholder="props.placeholder"
        :value="selectedSchool"
        @update:model-value="onUpdate"
        @search="onSearch"
        @open="isOpen = true"
        @close="onClose"
      >
        <template #search="{ attributes, events }">
          <input
            class="vs__search"
            v-bind="{
              ...attributes,
              'aria-labelledby': undefined,
              'aria-label': props.label,
            }"
            v-on="events"
          />
        </template>
        <template #no-options> No matching schools. </template>
        <template #option="school: School | null">
          <span
            v-if="school"
            class="flex w-full min-w-0 flex-col sm:flex-row sm:items-center sm:gap-x-1"
          >
            <span class="min-w-0 truncate" v-text="school.name" />
            <PayloadDocResolver v-slot="{ doc }" :doc="school.location.suburb">
              <span
                class="whitespace-normal opacity-25 sm:flex-none sm:whitespace-nowrap"
              >
                ({{ doc.name }}, {{ doc.state }})
              </span>
            </PayloadDocResolver>
          </span>
        </template>
      </VueSelect>
    </ClientOnly>
    <InputError :error="errorMessage" />
  </div>
</template>

<script setup lang="ts">
import type { InputProps } from './types'
import type { School } from '#payload-types'
import type { BaseInputProps } from '@/components/base/input/types'

interface InputSchoolProps extends BaseInputProps, InputProps {
  loading?: boolean
  schools: School[]
  rounded?: boolean
  icon?: 'school' | 'search'
  /** Seeds the box, for arrivals that already know what they're looking for. */
  initialQuery?: string
}

/** vue-select keeps its search text and open state in `data`, reachable off the instance. */
interface VueSelectInstance {
  search: string
  open: boolean
}

const props = withDefaults(defineProps<InputSchoolProps>(), {
  loading: false,
  placeholder: 'Find your school',
  type: 'small',
  icon: 'school',
})
const { id, value, errorMessage } = useInput<School | null>(props.name)
const { hasTouch } = useTouch()

const selectedSchool = ref<School | null>(value.value ?? null)

watch(
  () => value.value,
  (newValue) => {
    selectedSchool.value = newValue ?? null
    // A cleared selection resets the options too — the closed dropdown swallows
    // vue-select's own empty search, which would leave the last results behind.
    if (!newValue) emit('search', '')
  },
)

const isOpen = ref(false)

const onUpdate = (school: School) => {
  value.value = school || null
}

const emit = defineEmits<{
  search: [search: string]
  close: []
}>()

const onClose = () => {
  isOpen.value = false
  emit('close')
}

/**
 * A visitor redirected here from a slug that named several schools arrives with the name
 * already in hand, so the box shows it and opens on the matches rather than making them
 * retype what they just clicked. Watched rather than read once because the page is
 * kept alive — coming back with a different `?search=` has to re-seed it.
 */
const selectRef = useTemplateRef<VueSelectInstance>('selectRef')

watch(
  [selectRef, () => props.initialQuery],
  ([select, query]) => {
    if (!select || !query || select.search === query) return

    select.search = query
    select.open = true
  },
  { immediate: true },
)

const onSearch = (search: string) => {
  if (search || isOpen.value) {
    emit(
      'search',
      search
        .replace(/'/g, '')
        .replace(/[^\w\s]/g, ' ')
        .replace(/\s+/, ' ')
        .toLowerCase(),
    )
  }
}
</script>

<style scoped>
@reference '#css';

.select.v-select:deep(.vs__open-indicator) {
  @apply hidden;
}
</style>
